<template>
  <div class="products-carousel">
    <!-- Loading skeleton (shown while loading) -->
    <div v-if="isLoading" class="row">
      <div v-for="i in 4" :key="`skeleton-${i}`" class="col-md-3">
        <div class="product-skeleton">
          <div class="skeleton-img"></div>
          <div class="skeleton-text"></div>
          <div class="skeleton-text short"></div>
          <div class="skeleton-btn"></div>
        </div>
      </div>
    </div>

    <!-- Mobile/Tablet: Horizontal scrollable -->
    <div v-else-if="isMobile" class="mobile-scroll-container">
      <div class="horizontal-scroll" ref="horizontalScroll">
        <div 
          v-for="product in props.products" 
          :key="product.id" 
          class="product-scroll-item"
        >
          <ProductCard :product="product" />
        </div>
      </div>
      
      <!-- Mobile indicators -->
      <div class="mobile-indicators" v-if="props.products.length > 1">
        <button 
          v-for="(product, index) in props.products" 
          :key="`mobile-indicator-${index}`" 
          type="button" 
          :class="{ active: index === activeMobileSlide }" 
          @click="scrollToProduct(index)"
        ></button>
      </div>
    </div>

    <!-- Desktop: Carousel when products are loaded -->
    <div v-else class="position-relative">
      <div id="productsCarousel" class="carousel slide" data-bs-ride="carousel">
        <div class="carousel-inner">
          <div 
            v-for="(group, index) in productGroups" 
            :key="index" 
            class="carousel-item" 
            :class="{ active: index === activeSlide }"
          >
            <div class="row g-4 d-flex justify-content-center">
              <div 
                v-for="product in group" 
                :key="product.id" 
                class="col-6 col-md-3"
              >
                <ProductCard :product="product" />
              </div>
            </div>
          </div>
        </div>
        
        <!-- Carousel Indicators (Desktop only) -->
        <div class="carousel-indicators" v-if="productGroups.length > 1">
          <button 
            v-for="(_, index) in productGroups" 
            :key="`indicator-${index}`" 
            type="button" 
            data-bs-target="#productsCarousel" 
            :data-bs-slide-to="index" 
            :class="{ active: index === activeSlide }" 
            :aria-current="index === activeSlide" 
            :aria-label="`Slide ${index + 1}`"
            @click="setActiveSlide(index)"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, defineProps } from 'vue';
import ProductCard from '@/components/ProductCard.vue';

const props = defineProps({
  products: {
    type: Array,
    required: true
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  itemsPerSlide: {
    type: Number,
    default: 4
  }
});

// Track active slide index
const activeSlide = ref(0);
const activeMobileSlide = ref(0);

// Track if device is mobile/tablet
const isMobile = ref(window.innerWidth < 1024);

// Reference to horizontal scroll element
const horizontalScroll = ref(null);

// Group products into slides (for desktop)
const productGroups = computed(() => {
  const groups = [];
  const productsArr = Array.isArray(props.products) ? props.products : [];
  const itemsPerRow = props.itemsPerSlide;
  
  for (let i = 0; i < productsArr.length; i += itemsPerRow) {
    groups.push(productsArr.slice(i, i + itemsPerRow));
  }
  return groups;
});

// Manual slide navigation methods
const nextSlide = () => {
  activeSlide.value = (activeSlide.value + 1) % productGroups.value.length;
};

// Set active slide directly when clicking an indicator
const setActiveSlide = (index) => {
  activeSlide.value = index;
};

// Mobile scroll functions
const scrollToProduct = (index) => {
  if (!horizontalScroll.value) return;
  
  const productWidth = 300; // Width of each product card + gap
  const scrollPosition = index * productWidth;
  
  horizontalScroll.value.scrollTo({
    left: scrollPosition,
    behavior: 'smooth'
  });
  
  activeMobileSlide.value = index;
};

// Handle scroll event to update active indicator
const handleScroll = () => {
  if (!horizontalScroll.value || !isMobile.value) return;
  
  const scrollLeft = horizontalScroll.value.scrollLeft;
  const productWidth = 300;
  const currentIndex = Math.round(scrollLeft / productWidth);
  
  activeMobileSlide.value = Math.min(currentIndex, props.products.length - 1);
};

// Handle window resize
const handleResize = () => {
  isMobile.value = window.innerWidth < 1024;
};

// Watch for changes in products to reset carousel
watch(() => props.products, () => {
  activeSlide.value = 0;
  activeMobileSlide.value = 0;
});

let carouselInterval = null;

// Auto-rotate carousel (desktop only)
onMounted(() => {
  window.addEventListener('resize', handleResize);
  
  // Add scroll listener for mobile indicators
  if (horizontalScroll.value) {
    horizontalScroll.value.addEventListener('scroll', handleScroll);
  }
  
  if (!isMobile.value) {
    carouselInterval = setInterval(() => {
      if (productGroups.value.length > 1) {
        nextSlide();
      }
    }, 6000);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  
  if (horizontalScroll.value) {
    horizontalScroll.value.removeEventListener('scroll', handleScroll);
  }
  
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
});

// Restart interval when switching from mobile to desktop
watch(isMobile, (newVal) => {
  if (carouselInterval) {
    clearInterval(carouselInterval);
  }
  
  if (!newVal && productGroups.value.length > 1) {
    carouselInterval = setInterval(() => {
      nextSlide();
    }, 6000);
  }
});
</script>

<style scoped>
.products-carousel {
  position: relative;
  margin-bottom: 3rem;
  overflow: visible;
}

/* Mobile/Tablet horizontal scroll */
.mobile-scroll-container {
  position: relative;
  width: 100%;
  overflow: visible;
  padding-bottom: 3rem;
}

.horizontal-scroll {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  overflow-y: visible;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Hide scrollbar for Firefox */
  padding: 0.5rem 0 1rem 0;
  scroll-snap-type: x mandatory;
}

/* Hide scrollbar for webkit browsers */
.horizontal-scroll::-webkit-scrollbar {
  display: none;
}

.product-scroll-item {
  flex: 0 0 300px;
  min-width: 300px;
  max-width: 300px;
  scroll-snap-align: start;
}

/* Mobile indicators */
.mobile-indicators {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2;
}

.mobile-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-indicators button.active {
  background-color: var(--primary-color);
  width: 12px;
  height: 12px;
}

/* Desktop carousel */
.carousel-control-prev,
.carousel-control-next {
  width: 40px;
  height: 40px;
  background: var(--primary-color);
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.8;
}

.carousel-control-prev {
  left: -20px;
}

.carousel-control-next {
  right: -20px;
}

.carousel-control-prev-icon,
.carousel-control-next-icon {
  width: 20px;
  height: 20px;
}

.carousel-indicators {
  position: absolute;
  bottom: -60px;
  z-index: 2;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 5px;
  background-color: rgba(255, 255, 255, 0.3);
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.carousel-indicators button.active {
  background-color: var(--primary-color);
  width: 12px;
  height: 12px;
}

/* Skeleton loading styles */
.product-skeleton {
  background: var(--black-color-2);
  border-radius: 1rem;
  padding: 1rem;
  height: 320px;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.skeleton-img {
  height: 180px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.skeleton-text {
  height: 20px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-btn {
  height: 40px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-top: auto;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive adjustments */
@media (max-width: 1023px) {
  .mobile-scroll-container {
    margin: 0;
    padding-bottom: 3rem;
    width: 100%;
  }
  
  .horizontal-scroll {
    padding: 0.5rem 1rem 1rem 1rem;
    gap: 1rem;
  }
  
  .product-scroll-item {
    flex: 0 0 300px;
    min-width: 300px;
    max-width: 300px;
  }
}

@media (max-width: 767px) {
  .carousel-control-prev,
  .carousel-control-next {
    display: none;
  }
  
  .carousel-indicators {
    bottom: -30px;
  }
  
  .horizontal-scroll {
    padding: 0.5rem 0.75rem 1rem 0.75rem;
    gap: 0.75rem;
  }
  
  .product-scroll-item {
    flex: 0 0 280px;
    min-width: 280px;
    max-width: 280px;
  }
  
  .mobile-indicators {
    bottom: 0.5rem;
  }
}

@media (max-width: 480px) {
  .horizontal-scroll {
    padding: 0.5rem 0.5rem 1rem 0.5rem;
    gap: 0.5rem;
  }
  
  .product-scroll-item {
    flex: 0 0 270px;
    min-width: 270px;
    max-width: 270px;
  }
}
</style>
